import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, mainSiteUrl } from '@/mocks/homeData';

const FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/d9935mnk7gok24d4a94g';

const siteUrl = import.meta.env.VITE_SITE_URL;

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Hipobuy vs Kakobuy vs CSSBuy vs Superbuy vs Oopbuy Spreadsheet Comparison 2026',
  description: 'Complete platform comparison covering category coverage, update frequency, beginner friendliness, and pricing for Hipobuy, Kakobuy, CSSBuy, Superbuy, and Oopbuy spreadsheets.',
  image: 'https://readdy.ai/api/search-image?query=Modern%20product%20comparison%20workspace%20with%20multiple%20screens%20side%20by%20side%20showing%20data%20tables%20charts%20and%20product%20cards%20arranged%20on%20minimal%20concrete%20desk%20cool%20neutral%20lighting%20professional%20editorial%20photography%20clean%20aesthetic%20with%20soft%20shadows&width=1800&height=700&seq=comp-hero-v2-1a2b&orientation=landscape',
  author: {
    '@type': 'Organization',
    name: 'SpreadsheetHub',
  },
  publisher: {
    '@type': 'Organization',
    name: 'SpreadsheetHub',
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/16c9e81c106f6a445869e4a45760203f.jpeg',
    },
  },
  datePublished: '2026-07-11',
  dateModified: '2026-07-11',
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${siteUrl}/spreadsheet-comparison/`,
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Which spreadsheet platform has the best category coverage?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Hipobuy Spreadsheet leads with 14 categories, followed by Superbuy with 12, Kakobuy with 10, CSSBuy with 8, and Oopbuy with 7 categories.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is Superbuy Spreadsheet better for beginners than CSSBuy?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, Superbuy Spreadsheet is generally considered more beginner-friendly due to its cleaner interface, weekly updates, and more intuitive category organization compared to CSSBuy.',
      },
    },
    {
      '@type': 'Question',
      name: 'Should I use one spreadsheet or multiple platforms?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Using multiple spreadsheets gives you the best results. Hipobuy for broad discovery, Kakobuy for fast price checks, and CSSBuy or Superbuy for specialized niches. Cross-referencing across platforms is the recommended approach.',
      },
    },
  ],
};

const platformData = [
  {
    name: 'Hipobuy Spreadsheet',
    categories: 14,
    update: 'Weekly',
    beginner: 'Excellent',
    pricing: 'Competitive',
    highlights: 'Broadest category coverage, most frequent updates, excellent for discovery phase.',
    weaknesses: 'Can feel overwhelming due to sheer volume; newer sellers may be less vetted.',
  },
  {
    name: 'Kakobuy Spreadsheet',
    categories: 10,
    update: 'Bi-weekly',
    beginner: 'Very Good',
    pricing: 'Value-focused',
    highlights: 'Fast price scanning, clean filtering, great for finding budget-friendly options quickly.',
    weaknesses: 'Smaller overall catalog, bi-weekly updates mean some listings may be slightly dated.',
  },
  {
    name: 'CSSBuy Spreadsheet',
    categories: 8,
    update: 'Monthly',
    beginner: 'Good',
    pricing: 'Premium',
    highlights: 'Higher-quality individual listings, strong QC documentation, great for careful shoppers.',
    weaknesses: 'Fewer categories and monthly updates; not ideal if you need the latest drops quickly.',
  },
  {
    name: 'Superbuy Spreadsheet',
    categories: 12,
    update: 'Weekly',
    beginner: 'Very Good',
    pricing: 'Balanced',
    highlights: 'Clean UI, reliable update cadence, well-balanced between breadth and depth.',
    weaknesses: 'Some niche categories are less populated compared to Hipobuy or Kakobuy.',
  },
  {
    name: 'Oopbuy Spreadsheet',
    categories: 7,
    update: 'Monthly',
    beginner: 'Good',
    pricing: 'Mid-range',
    highlights: 'Focused niche specialization, particularly strong in accessories and jewelry categories.',
    weaknesses: 'Limited overall coverage; best used as a supplementary resource rather than primary.',
  },
];

export default function ComparisonPage() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full min-h-[360px] md:min-h-[440px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20product%20comparison%20workspace%20with%20multiple%20screens%20side%20by%20side%20showing%20data%20tables%20charts%20and%20product%20cards%20arranged%20on%20minimal%20concrete%20desk%20cool%20neutral%20lighting%20professional%20editorial%20photography%20clean%20aesthetic%20with%20soft%20shadows&width=1800&height=700&seq=comp-hero-v2-1a2b&orientation=landscape"
              alt="Spreadsheet Comparison Hub"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/45"></div>
          </div>
          <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 pt-20">
            <div className="max-w-3xl">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors mb-6"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line text-xs"></i>
                </div>
                Back to SpreadsheetHub
              </Link>
              <span className="inline-block px-3 py-1 rounded-full bg-white/15 text-white/90 text-xs font-medium font-label mb-4">
                Comparison Guide 2026
              </span>
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                Hipobuy vs Kakobuy vs CSSBuy vs Superbuy vs Oopbuy Spreadsheet Comparison
              </h1>
              <p className="mt-4 text-sm md:text-base text-white/70 leading-relaxed">
                Complete platform comparison covering category coverage, update frequency, beginner friendliness, and pricing for all major shopping spreadsheet platforms.
              </p>
            </div>
          </div>
        </section>

        {/* Article Body */}
        <article className="py-16 md:py-20 bg-background-50">
          <div className="w-full max-w-[900px] mx-auto px-6 md:px-10">
            {/* Intro */}
            <section className="mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                Choosing the Right Spreadsheet Platform in 2026
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                With so many spreadsheet resources available today, choosing the right platform for your shopping research can feel overwhelming. Each spreadsheet platform — <strong>Hipobuy</strong>, <strong>Kakobuy</strong>, <strong>CSSBuy</strong>, <strong>Superbuy</strong>, and <strong>Oopbuy</strong> — brings its own strengths, update cadence, and category focus. This guide breaks them down side by side so you can make an informed decision.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                Rather than treating one spreadsheet as the ultimate solution, experienced shoppers often combine multiple platforms for the best coverage. Understanding what each one does well — and where it falls short — is the key to building a smarter shopping workflow.
              </p>
            </section>

            {/* Comparison Table */}
            <section className="mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                Head-to-Head Comparison Table
              </h2>
              <div className="overflow-x-auto rounded-2xl border border-background-200/70 mb-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-100">
                      <th className="text-left px-5 py-3.5 font-semibold text-foreground-900 font-label text-xs">Platform</th>
                      <th className="text-left px-5 py-3.5 font-semibold text-foreground-900 font-label text-xs">Categories</th>
                      <th className="text-left px-5 py-3.5 font-semibold text-foreground-900 font-label text-xs">Updates</th>
                      <th className="text-left px-5 py-3.5 font-semibold text-foreground-900 font-label text-xs">Beginner</th>
                      <th className="text-left px-5 py-3.5 font-semibold text-foreground-900 font-label text-xs">Pricing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {platformData.map((row, i) => (
                      <tr key={i} className="border-t border-background-200/50">
                        <td className="px-5 py-3.5 text-foreground-800 font-medium">{row.name}</td>
                        <td className="px-5 py-3.5 text-foreground-500">{row.categories}</td>
                        <td className="px-5 py-3.5 text-foreground-500">{row.update}</td>
                        <td className="px-5 py-3.5">
                          <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${row.beginner === 'Excellent' ? 'bg-primary-100 text-primary-700' : row.beginner === 'Very Good' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-700'}`}>
                            {row.beginner}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-foreground-500">{row.pricing}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-foreground-500">
                Data based on independent research and community feedback as of July 2026. Always verify current availability directly.
              </p>
            </section>

            {/* Individual Platform Breakdown */}
            <section className="mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                Platform Deep Dive: What Makes Each One Unique
              </h2>
              <div className="space-y-6">
                {platformData.map((p) => (
                  <div key={p.name} className="rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">{p.name}</h3>
                      <span className="inline-block px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs font-medium">
                        {p.categories} Categories
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-label text-sm font-semibold text-primary-700 mb-1.5">Highlights</h4>
                        <p className="text-sm text-foreground-500 leading-relaxed">{p.highlights}</p>
                      </div>
                      <div>
                        <h4 className="font-label text-sm font-semibold text-foreground-700 mb-1.5">Weaknesses</h4>
                        <p className="text-sm text-foreground-500 leading-relaxed">{p.weaknesses}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Which One For You */}
            <section className="mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                Which Spreadsheet Platform Is Right for You?
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {[
                  { profile: 'Beginner Shoppers', pick: 'Hipobuy or Superbuy', reason: 'Both offer excellent category breadth and weekly updates. Hipobuy has the most categories to explore, while Superbuy balances breadth with a cleaner user experience.' },
                  { profile: 'Price-Conscious Buyers', pick: 'Kakobuy', reason: 'Kakobuy shines when you want fast price scanning. Its filtering is designed to surface budget-friendly finds without the noise of premium listings.' },
                  { profile: 'Quality-First Shoppers', pick: 'CSSBuy', reason: 'CSSBuy prioritizes listing quality over quantity. If you prefer fewer but better-documented products with strong QC records, CSSBuy is your best bet.' },
                  { profile: 'Niche Collectors', pick: 'Oopbuy', reason: 'Oopbuy specializes in accessories and jewelry spreadsheets. For buyers focused on these categories, it offers the most targeted and relevant data.' },
                  { profile: 'Cross-Platform Researchers', pick: 'All Five', reason: 'The most experienced spreadsheet users combine Hipobuy for discovery, Kakobuy for price checks, CSSBuy for quality verification, Superbuy for weekly updates, and Oopbuy for niche items.' },
                  { profile: 'Fast-Moving Trend Followers', pick: 'Hipobuy + Superbuy', reason: 'Weekly updates on both platforms mean you are among the first to catch new drops, restocks, and trending items as they hit the market.' },
                ].map((item) => (
                  <div key={item.profile} className="rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-6">
                    <span className="inline-block px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium font-label mb-3">
                      {item.profile}
                    </span>
                    <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2">
                      Recommended: {item.pick}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{item.reason}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Category Quick Links */}
            <section className="mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                Explore Product Categories
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                No matter which platform you choose, the ultimate goal is finding the right products. Here are all 14 categories you can explore across spreadsheet platforms:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                {categories.map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2.5 px-4 py-3 rounded-xl border border-background-200/70 bg-background-50 hover:border-primary-200 hover:bg-background-100 transition-all duration-200 cursor-pointer"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-folder-line text-primary-500 text-sm"></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-700 whitespace-nowrap">{cat.name}</span>
                  </a>
                ))}
              </div>
            </section>

            {/* Internal Articles */}
            <section className="mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                More Spreadsheet Research Guides
              </h2>
              <p className="text-sm text-foreground-600 mb-6">
                Dive deeper into individual platforms with our dedicated research articles:
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/best-hipobuy-spreadsheet-guide-2026/"
                  className="flex-1 rounded-2xl border border-background-200/70 bg-background-50 p-5 hover:border-primary-200 transition-all duration-200"
                >
                  <h3 className="font-heading text-base font-bold text-foreground-900 mb-1">Best Hipobuy Spreadsheet Guide 2026</h3>
                  <p className="text-sm text-foreground-500">Complete guide for product discovery and QC verification.</p>
                </Link>
                <Link
                  to="/kakobuy-spreadsheet-guide/"
                  className="flex-1 rounded-2xl border border-background-200/70 bg-background-50 p-5 hover:border-primary-200 transition-all duration-200"
                >
                  <h3 className="font-heading text-base font-bold text-foreground-900 mb-1">Kakobuy Spreadsheet Guide</h3>
                  <p className="text-sm text-foreground-500">Fast price scanning and value-focused shopping.</p>
                </Link>
                <Link
                  to="/cssbuy-spreadsheet-guide/"
                  className="flex-1 rounded-2xl border border-background-200/70 bg-background-50 p-5 hover:border-primary-200 transition-all duration-200"
                >
                  <h3 className="font-heading text-base font-bold text-foreground-900 mb-1">CSSBuy Spreadsheet Guide</h3>
                  <p className="text-sm text-foreground-500">Quality-first approach with strong QC documentation.</p>
                </Link>
              </div>
              <div className="mt-3 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/superbuy-spreadsheet-guide/"
                  className="flex-1 rounded-2xl border border-background-200/70 bg-background-50 p-5 hover:border-primary-200 transition-all duration-200"
                >
                  <h3 className="font-heading text-base font-bold text-foreground-900 mb-1">Superbuy Spreadsheet Guide</h3>
                  <p className="text-sm text-foreground-500">Weekly updates with balanced breadth and depth.</p>
                </Link>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex-1 rounded-2xl border border-background-200/70 bg-background-50 p-5 hover:border-primary-200 transition-all duration-200 cursor-pointer"
                >
                  <h3 className="font-heading text-base font-bold text-foreground-900 mb-1">Explore Products on Main Site</h3>
                  <p className="text-sm text-foreground-500">Browse 14 product categories on kaspreadsheet.com.</p>
                </a>
              </div>
            </section>

            {/* FAQ */}
            <section className="mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                Frequently Asked Questions About Spreadsheet Comparison
              </h2>
              <div className="space-y-4">
                {[
                  { q: 'Which spreadsheet platform has the best category coverage?', a: 'Hipobuy Spreadsheet leads with 14 categories, followed by Superbuy with 12, Kakobuy with 10, CSSBuy with 8, and Oopbuy with 7. For shoppers who want the widest selection, Hipobuy and Superbuy are the top choices.' },
                  { q: 'Is Superbuy Spreadsheet better for beginners than CSSBuy?', a: 'Yes, Superbuy Spreadsheet is generally considered more beginner-friendly due to its cleaner interface, weekly updates, and more intuitive category organization. CSSBuy is excellent but slightly more focused on quality than ease of use.' },
                  { q: 'Should I use one spreadsheet or multiple platforms?', a: 'Using multiple spreadsheets gives you the best results. Hipobuy for broad discovery, Kakobuy for fast price checks, and CSSBuy or Superbuy for specialized niches. Cross-referencing across platforms is the recommended approach for serious shoppers.' },
                  { q: 'Which platform updates most frequently?', a: 'Hipobuy and Superbuy both update weekly, making them ideal for catching new drops and restocks. Kakobuy updates bi-weekly, while CSSBuy and Oopbuy update monthly.' },
                  { q: 'Is there a cost difference between platforms?', a: 'All spreadsheet resources themselves are free to access. Cost differences come from the product pricing on the actual shopping platforms. Kakobuy tends to surface more value-priced options, while CSSBuy and Superbuy include a wider range including premium selections.' },
                ].map((faq, i) => (
                  <div key={i} className="rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-6">
                    <h3 className="font-heading text-base md:text-lg font-bold text-foreground-900 mb-2">{faq.q}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{faq.a}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* CTA */}
            <section className="mb-10 rounded-3xl bg-background-100 border border-background-200/70 p-8 md:p-12 text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                Ready to Start Comparing?
              </h2>
              <p className="text-sm md:text-base text-foreground-500 mb-6 max-w-lg mx-auto">
                Explore all product categories and find the best deals across every spreadsheet platform.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center px-7 py-3 rounded-full bg-foreground-900 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Hipobuy Sheet
                  <div className="w-4 h-4 flex items-center justify-center ml-2">
                    <i className="ri-arrow-right-up-line text-sm"></i>
                  </div>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center px-7 py-3 rounded-full border-2 border-foreground-900 text-foreground-900 text-sm font-semibold hover:bg-foreground-900 hover:text-background-50 transition-all duration-200 whitespace-nowrap"
                >
                  Back to Homepage
                </Link>
              </div>
            </section>

            {/* Internal link back */}
            <div className="text-center pb-6">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-foreground-400 hover:text-foreground-700 transition-colors"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-home-line text-xs"></i>
                </div>
                Return to SpreadsheetHub Homepage
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer formSubmitAddr={FORM_SUBMIT_ADDR} />
    </>
  );
}